// Avisos de la escritura estándar (switch "Estandarizar marcas y modelos").
//
// AvisoMarcasEstandar, para las cargas por lote (compra por lote, importación
// Excel, packing list): qué marcas/modelos se guardarán con la escritura
// estándar y cuáles parecen mal escritos ("¿Quisiste decir ...?"). Agrupa
// por par distinto y no bloquea nada. Con `onUsar(par, sugerencia)` cada
// sugerencia trae un botón para aplicarla a las filas de ese par.
//
// VistaPreviaMarca, para el formulario de una sola unidad: la línea debajo
// de marca/modelo con "Se guardará como ..." o "¿Quisiste decir ...? Usar".
//
// Sin nada que mostrar, o con el switch apagado, no pintan nada.

import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";

const VISIBLES = 5;
const ESPERA_MS = 600;

export function VistaPreviaMarca({ vista, onUsar }) {
  if (!vista) return null;
  const s = vista.sugerencia;
  return (
    <Typography variant="caption" component="div" sx={{ color: "info.main", fontWeight: 600 }}>
      {s ? (
        <>
          ¿Quisiste decir {s.marca} · {s.modelo}?{" "}
          <Button
            size="small"
            onClick={() => onUsar(s)}
            sx={{ py: 0, minWidth: 0, fontSize: "inherit" }}
          >
            Usar
          </Button>
        </>
      ) : (
        `Se guardará como ${vista.marca} · ${vista.modelo}`
      )}
    </Typography>
  );
}

VistaPreviaMarca.defaultProps = { vista: null };

VistaPreviaMarca.propTypes = {
  vista: PropTypes.shape({
    marca: PropTypes.string,
    modelo: PropTypes.string,
    sugerencia: PropTypes.shape({ marca: PropTypes.string, modelo: PropTypes.string }),
  }),
  onUsar: PropTypes.func.isRequired,
};

export default function AvisoMarcasEstandar({ filas, estandar, onUsar, sx }) {
  const [cambios, setCambios] = useState([]);
  const [dudas, setDudas] = useState([]);
  const [abierto, setAbierto] = useState(false);

  // Pares distintos (lo que importa es la escritura, no cuántas filas).
  const pares = useMemo(() => {
    const vistos = new Map();
    (filas || []).forEach((f) => {
      const marca = String(f?.marca ?? "").trim();
      const modelo = String(f?.modelo ?? "").trim();
      if (!marca) return;
      const k = `${marca}\u0000${modelo}`;
      const prev = vistos.get(k);
      if (prev) prev.filas += 1;
      else vistos.set(k, { marca, modelo, filas: 1 });
    });
    return [...vistos.values()];
  }, [filas]);

  const { activo, resolver } = estandar;
  useEffect(() => {
    if (!activo || !pares.length) {
      setCambios([]);
      setDudas([]);
      return undefined;
    }
    let vigente = true;
    const t = setTimeout(() => {
      resolver(pares.map(({ marca, modelo }) => ({ marca, modelo }))).then((res) => {
        if (!vigente) return;
        const todos = res.map((r, i) => ({ ...pares[i], nueva: r }));
        setDudas(todos.filter((x) => x.nueva && x.nueva.sugerencia));
        setCambios(todos.filter((x) => x.nueva && x.nueva.cambio && !x.nueva.sugerencia));
      });
    }, ESPERA_MS);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [activo, pares, resolver]);

  if (!cambios.length && !dudas.length) return null;
  const total = cambios.reduce((s, c) => s + c.filas, 0);
  const lista = abierto ? cambios : cambios.slice(0, VISIBLES);
  return (
    <>
      {dudas.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1, ...sx }}>
          Posibles errores de escritura (se guardan como están si no se corrigen):
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {dudas.map((c) => (
              <li key={`${c.marca}|${c.modelo}`}>
                {c.marca} · {c.modelo || "(sin modelo)"}: ¿quisiste decir{" "}
                <b>
                  {c.nueva.sugerencia.marca} · {c.nueva.sugerencia.modelo}
                </b>
                ?{c.filas > 1 ? ` (${c.filas} filas)` : ""}
                {onUsar && (
                  <Button
                    size="small"
                    onClick={() => onUsar({ marca: c.marca, modelo: c.modelo }, c.nueva.sugerencia)}
                    sx={{ ml: 0.5, py: 0, minWidth: 0 }}
                  >
                    Usar
                  </Button>
                )}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {cambios.length > 0 && (
        <Alert severity="info" sx={{ mb: 1, ...sx }}>
          {total === 1 ? "1 fila se guardará" : `${total} filas se guardarán`} con la escritura
          estándar:
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {lista.map((c) => (
              <li key={`${c.marca}|${c.modelo}`}>
                {c.marca} · {c.modelo || "(sin modelo)"} →{" "}
                <b>
                  {c.nueva.marca} · {c.nueva.modelo}
                </b>
                {c.filas > 1 ? ` (${c.filas} filas)` : ""}
              </li>
            ))}
          </Box>
          {cambios.length > VISIBLES && (
            <Button
              size="small"
              onClick={() => setAbierto((v) => !v)}
              sx={{ mt: 0.5, p: 0, minWidth: 0 }}
            >
              {abierto ? "Ver menos" : `Ver los ${cambios.length}`}
            </Button>
          )}
        </Alert>
      )}
    </>
  );
}

// Las celdas de Excel pueden llegar como número.
const celda = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

AvisoMarcasEstandar.defaultProps = { filas: [], onUsar: null, sx: {} };

AvisoMarcasEstandar.propTypes = {
  filas: PropTypes.arrayOf(PropTypes.shape({ marca: celda, modelo: celda })),
  estandar: PropTypes.shape({ activo: PropTypes.bool, resolver: PropTypes.func }).isRequired,
  onUsar: PropTypes.func,
  sx: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};
